import React, { Fragment, useEffect, useState } from 'react'
import { Edit, Delete, Visibility } from '@mui/icons-material'
import { StandardPage } from '../../components/_EOS/StandardPage'
import { FilterComponent } from '../../components/_WMS/FilterComponent'
import { GenericTableV2 } from '../../components/_EOS/GenericTable/GenericTableV2'
import { Modal } from '../../components/_EOS/Modal'
import { SnackBar } from '../../components/SnackBar/Snackbar'

import { useApi } from '../../hooks/useApi'
import { usePagination } from '../../hooks/usePagination'
import { useModal } from '../../hooks/useModal'
import { useSnackbar } from '../../hooks/useSnackbar'

import { filterOptions } from './config/filterOptions'
import { getColumnsConfig } from './config/tableColumns'
import {
  getSupplierOrigins,
  createSupplierOrigin,
  updateSupplierOrigin,
  deleteSupplierOrigin
} from './config/api'
import { SupplierOriginForm } from './components/SupplierOriginForm'
import { ViewSupplierOrigin } from './components/ViewSupplierOrigin'

import { SupplierOrigin } from '../../interfaces/Supplier/SupplierOrigin/SupplierOrigin'
import { Filter } from '../../components/_WMS/FilterComponent/types'
import { Status } from '../../hooks/useFetchState'
import { ApiResponse } from '../../typings/ApiResponse'
import { GenericOrderBy } from '../../components/_EOS/Table/types'
import { calculatePageAfterDelete } from '../../utils/paginationHelpers'
import { useSuppliersOrigin } from '../../hooks/useSuppliersOrigin'

export const SupplierOriginPage: React.FC = () => {
  const { callApi, state } = useApi<ApiResponse<SupplierOrigin>>()
  const pagination = usePagination(10)
  const { isActive, message, openSnackBar, isSuccess } = useSnackbar()

  const [selectedFilters, setSelectedFilters] = useState<Filter[]>([])

  // Estado de ordenamiento
  const [orderBy, setOrderBy] = useState<GenericOrderBy<SupplierOrigin>>({
    field: 'supplierOriginName' as keyof SupplierOrigin,
    direction: 'asc'
  })

  const createModal = useModal('Crear Origen')
  const editModal = useModal<SupplierOrigin>('Editar Origen')
  const deleteModal = useModal<SupplierOrigin>('Confirmar Eliminación')
  const viewModal = useModal<SupplierOrigin>('Información del origen')

  const columnsConfig = getColumnsConfig()

  const { getSuppliersOrigin } = useSuppliersOrigin()

  const requestData = (filters: Filter[], page: number, pageSize: number) => {
    callApi(getSupplierOrigins(filters, page, pageSize, orderBy)).catch(
      (error) => {
        openSnackBar(error, false)
      }
    )
  }

  const handleSorting = (
    field: keyof SupplierOrigin,
    direction: 'asc' | 'desc'
  ) => {
    setOrderBy({ field, direction })
  }

  const handlePagination = (page: number, pageSize: number) => {
    const auxPage = pagination.pageSize !== pageSize ? 0 : page
    requestData(selectedFilters, auxPage, pageSize)
    pagination.setPagination(auxPage, pageSize)
  }

  const handleFilterChange = (value: React.SetStateAction<Filter[]>) => {
    let newFilters: Filter[]

    if (typeof value === 'function') {
      newFilters = value(selectedFilters)
    } else {
      newFilters = value
    }

    setSelectedFilters(newFilters)

    // Si se limpiaron todos los filtros, buscar automáticamente
    if (newFilters.length === 0 && selectedFilters.length > 0) {
      requestData([], pagination.page, pagination.pageSize)
    }
  }

  const handleCreate = (values: SupplierOrigin) => {
    callApi(createSupplierOrigin(values))
      .then(() => {
        openSnackBar('Origen creado correctamente', true)
        requestData(selectedFilters, pagination.page, pagination.pageSize)
        getSuppliersOrigin()
        createModal.closeModal()
      })
      .catch((error) => {
        openSnackBar(error, false)
      })
  }

  const handleEdit = (values: SupplierOrigin) => {
    callApi(updateSupplierOrigin(values.id, values))
      .then(() => {
        openSnackBar('Origen actualizado correctamente', true)
        requestData(selectedFilters, pagination.page, pagination.pageSize)
        getSuppliersOrigin()
        editModal.closeModal()
      })
      .catch((error) => {
        openSnackBar(error, false)
      })
  }

  const handleDelete = (id: number) => {
    const currentTotal = state.data?.totalItems || 0

    callApi(deleteSupplierOrigin(id))
      .then(() => {
        openSnackBar('Origen eliminado correctamente', true)

        // Calcular la página correcta después de eliminar
        const newPage = calculatePageAfterDelete(
          pagination.page,
          pagination.pageSize,
          currentTotal,
          1
        )

        // Actualizar paginación si cambió la página
        if (newPage !== pagination.page) {
          pagination.setPagination(newPage, pagination.pageSize)
        }

        // Hacer request con la página correcta
        requestData(selectedFilters, newPage, pagination.pageSize)
        deleteModal.closeModal()
      })
      .catch((error) => {
        openSnackBar(error, false)
      })
  }

  const handleCreateClick = () => {
    createModal.openModal('md')
  }

  useEffect(() => {
    // Solo buscar automáticamente en la carga inicial, no cuando cambian los filtros
    requestData([], pagination.page, pagination.pageSize)
  }, [pagination.pageSize])

  useEffect(() => {
    pagination.setPagination(0, pagination.pageSize)

    requestData(selectedFilters, pagination.page, pagination.pageSize)
  }, [orderBy])

  return (
    <Fragment>
      <StandardPage
        pageTitle='Gestionar Origenes'
        buttonAction={handleCreateClick}
        buttonTitle='Crear Origen'
      >
        <FilterComponent
          includeAddButton={true}
          options={filterOptions}
          onSearchClick={() =>
            requestData(selectedFilters, pagination.page, pagination.pageSize)
          }
          selectedFilters={selectedFilters}
          setSelectedFilters={handleFilterChange}
        />

        <GenericTableV2<SupplierOrigin>
          data={state.data?.items || []}
          columns={columnsConfig}
          keyField='id'
          loading={state.status === Status.Loading}
          pagination={{
            page: pagination.page,
            pageSize: pagination.pageSize,
            total: state.data?.totalItems || 0,
            onPageChange: handlePagination
          }}
          sorting={{
            field: orderBy.field as keyof SupplierOrigin,
            direction: orderBy.direction,
            onSortChange: handleSorting
          }}
          actions={[
            {
              key: 'edit',
              label: 'Editar',
              onClick: (item) => editModal.openModal('md', item),
              icon: <Edit />
            },
            {
              key: 'view',
              label: 'Ver',
              onClick: (item) => viewModal.openModal('md', item),
              icon: <Visibility />
            },
            {
              key: 'delete',
              label: 'Eliminar',
              onClick: (item) => deleteModal.openModal('sm', item),
              icon: <Delete />
            }
          ]}
        />
      </StandardPage>

      <Modal
        open={createModal.isOpen}
        maxWidth={createModal.size}
        title={createModal.title}
        onClose={createModal.closeModal}
      >
        <SupplierOriginForm onSubmit={handleCreate} />
      </Modal>

      <Modal
        open={editModal.isOpen}
        maxWidth={editModal.size}
        title={editModal.title}
        onClose={editModal.closeModal}
      >
        <SupplierOriginForm
          initialData={editModal.extendedProps}
          onSubmit={handleEdit}
          isEditing={true}
        />
      </Modal>

      <Modal
        open={deleteModal.isOpen}
        maxWidth={deleteModal.size}
        title={deleteModal.title}
        onClose={deleteModal.closeModal}
        loading={state.status === Status.Loading}
        successButton={{
          action: () => handleDelete(deleteModal.extendedProps?.id || 0),
          title: 'Confirmar'
        }}
        cancelButton={{
          action: () => deleteModal.closeModal(),
          title: 'Cancelar'
        }}
      >
        ¿Desea eliminar el origen{' '}
        <strong>{deleteModal.extendedProps?.supplierOriginName}</strong> del
        proveedor <strong>{deleteModal.extendedProps?.supplierName}</strong>?
      </Modal>

      <Modal
        open={viewModal.isOpen}
        maxWidth={viewModal.size}
        title={viewModal.title}
        onClose={viewModal.closeModal}
      >
        <ViewSupplierOrigin supplierOrigin={viewModal.extendedProps} />
      </Modal>

      <SnackBar
        message={message}
        status={isSuccess ? 'success' : 'error'}
        open={isActive}
      />
    </Fragment>
  )
}
